import { useDispatch, useSelector } from "react-redux";
import CustomPage from "components/CustomPage";
import { useState } from "react";
import DatesReports from "components/CustomSearch";
import moment from "moment";
import NullCheck from "components/NullCheck";
import http from "../../../../http";
import { DATE_FOR_API_FORMAT, MC_DEVICE_TYPE, MC_REPORTS_DHT } from "config/const";
import { apiUrl } from "config/settings";
import { setShowLoading, setShowMassage } from "store/sysSlice";
import ChartHoursAvg from "./ChartHoursAvg";
import CustomDataGrid from "components/customDataGrid";
import { useBizTypeTranslation } from "hooks/useBizTypeTranslation";
import CustomCard from "components/CustomCard";
import { getIconByDevice } from "containers/controlAndMonitoring/const";
import { findKeyByValue } from "config/functions";
import { CalendarMonth, FmdGoodOutlined } from "@mui/icons-material";
import { IconButton, Stack, Typography } from "@mui/material";

const McAvgDHTReport = () => {
  const dispatch = useDispatch();
  const { bt } = useBizTypeTranslation();
  const mcList = useSelector((state) => state?.mc.mcList);
  const [datesReports, setDatesReports] = useState(true);
  const [list, setList] = useState([]);
  const [data, setData] = useState(null);
  const [gridDataList, setGridDataList] = useState([]);
  const [hasTwoDHTSensors, setHasTwoDHTSensors] = useState(false);

  const callBackDate = async (data) => {
    if (!data?.selectedMc) {
      dispatch(setShowLoading(false));
      dispatch(setShowMassage({ massage: "SELECT_MC_ERROR", type: "error" }));
      setTimeout(() => setDatesReports(true), 100);
      return
    }
    if (data) {
      dispatch(setShowLoading(true));
      const dates = {
        start: moment(data?.startDate).startOf('day').format(DATE_FOR_API_FORMAT),
        end: moment(data?.endDate).endOf('day')?.format(DATE_FOR_API_FORMAT),
      };

      http
        .get(`${apiUrl}/reports/avgDHT/${data?.selectedMc}/${dates?.start}/${dates?.end}`)
        .then((res) => {
          dispatch(setShowLoading(false));
          const resData = {
            dates: {
              start: moment(data?.startDate).startOf('day'),
              end: moment(data?.endDate).endOf('day'),
            },
            avgs: res?.data?.data,
            mcData: mcList?.find((item) => item?.guid === data?.selectedMc),
          }
          setList(res?.data?.data);
          setHasTwoDHTSensors(res?.data?.data?.some(item => item?.DHT2 > 0));
          setData(resData);
        });
    }
  };

  function getDateRanges(startDate, endDate, type = "day") {

    const start = startDate.clone().startOf("day");
    const end = endDate.clone().startOf("day");

    const ranges = [];

    if (start.isAfter(end)) return [];

    if (type === "day") {
      let current = start.clone();

      while (current.isSameOrBefore(end, "day")) {
        ranges.push({ startDate: current.format(DATE_FOR_API_FORMAT), endDate: current.format(DATE_FOR_API_FORMAT) });
        current.add(1, "day");
      }
    }

    else if (type === "week") {
      let current = start.clone().startOf("week");

      while (current.isSameOrBefore(end, "day")) {
        const rangeStart = moment.max(current.clone(), start.clone());
        const rangeEnd = moment.min(current.clone().endOf("week"), end.clone());
        ranges.push({ startDate: rangeStart, endDate: rangeEnd });

        current.add(1, "week");
      }
    }

    else if (type === "month") {
      let current = start.clone().startOf("month");

      while (current.isSameOrBefore(end, "day")) {
        const rangeStart = moment.max(current.clone(), start.clone());
        const rangeEnd = moment.min(current.clone().endOf("month"), end.clone());

        ranges.push({ startDate: rangeStart, endDate: rangeEnd });

        current.add(1, "month");
      }
    }

    return ranges;
  }

  return (
    <CustomPage hideTitle >
      <Stack spacing={2} mb={2}>

        <CustomCard width="100%" direction="row" pb={1} padding={1} alignItems="center" justifyContent="space-between">

          <Stack direction={{ xs: "column", md: "row" }} justifyContent='space-between'>
            <Stack spacing={1}>
              <Typography color="primary.text">
                {data?.mcData.name}
              </Typography>

              <Stack direction="row" spacing={{ xs: 1, md: 2 }}>
                <NullCheck valueForCheck={data?.mcData?.deviceType !== MC_DEVICE_TYPE.NONE}>
                  <Stack alignItems="center" direction="row" spacing={0.5}>
                    {getIconByDevice(data?.mcData?.deviceType)}
                    <Typography variant="body2" color="primary.text">
                      {bt(findKeyByValue(MC_DEVICE_TYPE, data?.mcData?.deviceType))}
                    </Typography>
                  </Stack>
                </NullCheck>

                <NullCheck valueForCheck={data?.mcData?.area?.name}>
                  <Stack alignItems="center" direction="row" spacing={0.5}>
                    <FmdGoodOutlined fontSize="small" sx={{ color: "primary.main" }} />
                    <Typography variant="body2" color="primary.text">
                      {data?.mcData?.area?.name}
                    </Typography>
                  </Stack>
                </NullCheck>
              </Stack>
            </Stack>
            <Stack />
          </Stack>

          <IconButton onClick={() => setDatesReports(true)}>
            <CalendarMonth />
          </IconButton>
        </CustomCard>

        <NullCheck valueForCheck={list?.length > 0} noData='NO_DATA'>
          <CustomCard spacing={0} padding={0}>

            <ChartHoursAvg
              padding={0}
              data={data}
              getDateRanges={getDateRanges}
              setGridDataList={setGridDataList}
            />

            <CustomDataGrid
              pageSize={12}
              columns={[
                {
                  field: "hour",
                  headerName: bt("ONE_HOUR"),
                  flex: 1,
                  renderCell: (value) => value?.row?.hour,
                },
                {
                  field: "DHT1",
                  headerName: bt("TEMP1"),
                  flex: 1,
                  renderCell: (value) => `${value?.row?.DHT1}%`,
                },
                {
                  field: "DHT2",
                  headerName: bt("TEMP2"),
                  flex: 1,
                  hide: !hasTwoDHTSensors,
                  renderCell: (value) => `${value?.row?.DHT2}%`,
                },
              ]
              }
              list={gridDataList?.map((item, index) => ({ id: index, ...item }))}
            />
          </CustomCard>

        </NullCheck>
      </Stack>


      <DatesReports
        btnTitle="CREATE_REPORT"
        title='DATES'
        mcList={mcList
          ?.filter(item => MC_REPORTS_DHT.includes(item?.softwareType))
          ?.map((item) => ({ guid: item?.guid, name: item?.name }))
        }
        open={datesReports}
        setOpen={setDatesReports}
        callBack={callBackDate}
      />
    </CustomPage>
  );
};
export default McAvgDHTReport;
